import { parseISO8601WithTimezone } from '@shared/components/ScheduleEditor/components/form/hooks/useGetEventTimezone';
import { ScheduleItemProps } from './components/ScheduleDisplay';
import { formatScheduleItemDate, ScheduleItemDateParts } from '@shared/utils/scheduleFormatting';
import { QuickTranslate } from '@shared/core/i18n/useTranslation';
import { formatDateForLanguage } from '@shared/hooks/useFormattedDate';

type ScheduleDateType = ScheduleItemProps['data']['startTimeISO8601TZ'];

/**
 * Helper to parse ISO8601 date string with timezone annotation and format into schedule date parts.
 * ScheduleDateType includes timezone in the format: "2025-10-06T17:00:00.000Z[America/Phoenix]"
 */
const parseScheduleDate = (date: ScheduleDateType, locale?: string): ScheduleItemDateParts | null => {
  if (!date) return null;
  // parseISO8601WithTimezone extracts and converts the UTC timestamp to the schedule item's timezone
  // The datetime is shifted so Intl.DateTimeFormat displays it correctly regardless of viewer's timezone
  const { datetime } = parseISO8601WithTimezone(date);
  return formatScheduleItemDate(datetime, locale);
};

/**
 * Formats schedule item dates as a time range string.
 * Handles TBA, single time, same-day range, and multi-day range cases.
 *
 * @param tq - Quick translate function for inline translations
 * @param startDate - Optional start date in ISO8601 format with timezone
 * @param endDate - Optional end date in ISO8601 format with timezone
 * @param locale - Optional BCP 47 language tag (defaults to 'en-US')
 * @returns Formatted time range string
 *
 * @example
 * formatDateAsTimes(tq, undefined, undefined, 'en-US')
 * // Returns: "TBA"
 *
 * @example
 * formatDateAsTimes(tq, "2025-11-11T20:00:00-08:00", undefined, 'en-US')
 * // Returns: "8:00 PM"
 *
 * @example
 * formatDateAsTimes(tq, "2025-11-11T20:00:00-08:00", "2025-11-11T23:00:00-08:00", 'en-US')
 * // Returns: "8:00 PM to 11:00 PM"
 *
 * @example
 * formatDateAsTimes(tq, "2025-11-11T20:00:00-08:00", "2025-11-12T02:00:00-08:00", 'en-US')
 * // Returns: "Nov 11 8:00 PM to\nNov 12 2:00 AM"
 */
export const formatDateAsTimes = (tq: QuickTranslate, startDate?: ScheduleDateType, endDate?: ScheduleDateType, locale?: string) => {
  const start = parseScheduleDate(startDate, locale);
  const end = parseScheduleDate(endDate, locale);

  if (!start) return tq`TBA`;

  if (!end) {
    return start.time;
  }

  // Detect multi-day events by comparing formatted date strings.
  // Since both dates are formatted with the same locale and timezone, identical strings indicate the same day.
  const isMultiDay = start.date !== end.date || start.year !== end.year;

  if (isMultiDay) {
    return tq`${start.date} ${start.time} to\n${end.date} ${end.time}`;
  }

  return tq`${start.time} to ${end.time}`;
};

export const formatDateAsDay = (dayDate: ScheduleDateType, locale: string = 'en-US') => {
  if (!dayDate) return null;
  const { datetime } = parseISO8601WithTimezone(dayDate);

  return formatDateForLanguage(locale, datetime, 'weekday, dd MMMM yyyy');
};
